import { useEffect, useRef } from 'react';

interface LightningBolt {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  branches: LightningBranch[];
  opacity: number;
  phase: number;
  speed: number;
  lifetime: number;
  age: number;
}

interface LightningBranch {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  thickness: number;
  segments: { x: number; y: number }[];
}

const CosmicLightningLayer = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('CosmicLightningLayer: Canvas ref is null');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('CosmicLightningLayer: Could not get 2D context');
        return;
      }
    
    let animationFrameId: number;
    let lightningBolts: LightningBolt[] = [];
    let lastTime = 0;
    let isPaused = document.hidden;
    let scrollTimeout: number;

    const onVisibilityChange = () => {
      isPaused = document.hidden;
    };

    const onScroll = () => {
      isPaused = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        isPaused = false;
      }, 120);
    };
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    const createLightningPath = (startX: number, startY: number, endX: number, endY: number): { x: number; y: number }[] => {
      const segments: { x: number; y: number }[] = [];
      const distance = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
      const segmentCount = Math.floor(distance / 20); // Segment every 20px
      
      for (let i = 0; i <= segmentCount; i++) {
        const t = i / segmentCount;
        const baseX = startX + (endX - startX) * t;
        const baseY = startY + (endY - startY) * t;
        
        // Add organic variation with stronger displacement in the middle
        const displacement = Math.sin(t * Math.PI) * 15; // Peak displacement at middle
        const randomOffset = (Math.random() - 0.5) * displacement;
        const perpX = -(endY - startY) / distance;
        const perpY = (endX - startX) / distance;
        
        segments.push({
          x: baseX + perpX * randomOffset,
          y: baseY + perpY * randomOffset
        });
      }
      
      return segments;
    };
    
    const createBranches = (mainPath: { x: number; y: number }[], startIndex: number): LightningBranch[] => {
      const branches: LightningBranch[] = [];
      
      // Only create 1-2 branches per bolt for gentleness
      const branchCount = Math.random() > 0.6 ? 2 : 1;
      
      for (let i = 0; i < branchCount; i++) {
        const branchStartIdx = Math.floor(startIndex + Math.random() * (mainPath.length - startIndex) * 0.7);
        if (branchStartIdx >= mainPath.length) continue;
        
        const startPoint = mainPath[branchStartIdx];
        const branchLength = 30 + Math.random() * 50;
        const angle = (Math.random() - 0.5) * Math.PI * 0.6; // ±54 degrees
        
        const endX = startPoint.x + Math.cos(angle) * branchLength;
        const endY = startPoint.y + Math.sin(angle) * branchLength;
        
        branches.push({
          startX: startPoint.x,
          startY: startPoint.y,
          endX,
          endY,
          thickness: 0.5 + Math.random() * 0.5,
          segments: createLightningPath(startPoint.x, startPoint.y, endX, endY)
        });
      }
      
      return branches;
    };
    
    const createLightningBolt = (): LightningBolt => {
      const side = Math.random();
      let startX: number, startY: number, endX: number, endY: number;
      
      // Lightning can start from any edge and move gently across
      if (side < 0.25) {
        // From left
        startX = -50;
        startY = Math.random() * canvas.height;
        endX = canvas.width * (0.3 + Math.random() * 0.4);
        endY = Math.random() * canvas.height;
      } else if (side < 0.5) {
        // From right
        startX = canvas.width + 50;
        startY = Math.random() * canvas.height;
        endX = canvas.width * (0.3 + Math.random() * 0.4);
        endY = Math.random() * canvas.height;
      } else if (side < 0.75) {
        // From top
        startX = Math.random() * canvas.width;
        startY = -50;
        endX = Math.random() * canvas.width;
        endY = canvas.height * (0.3 + Math.random() * 0.4);
      } else {
        // From bottom
        startX = Math.random() * canvas.width;
        startY = canvas.height + 50;
        endX = Math.random() * canvas.width;
        endY = canvas.height * (0.3 + Math.random() * 0.4);
      }
      
      const mainPath = createLightningPath(startX, startY, endX, endY);
      const branches = createBranches(mainPath, Math.floor(mainPath.length * 0.2));
      
      return {
        x: startX,
        y: startY,
        targetX: endX,
        targetY: endY,
        branches: [
          {
            startX,
            startY,
            endX,
            endY,
            thickness: 1.5 + Math.random() * 0.5,
            segments: mainPath
          },
          ...branches
        ],
        opacity: 0,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 0.2,
        lifetime: 4000 + Math.random() * 2000,
        age: 0
      };
    };
    
    const drawLightning = (ctx: CanvasRenderingContext2D, bolt: LightningBolt) => {
      bolt.branches.forEach((branch, branchIndex) => {
        const isMainBolt = branchIndex === 0;
        const baseOpacity = bolt.opacity * (isMainBolt ? 1 : 0.7);
        
        // Outer magenta glow
        ctx.strokeStyle = `hsla(322, 100%, 65%, ${baseOpacity * 0.18})`;
        ctx.lineWidth = branch.thickness * 8;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        if (branch.segments.length > 0) {
          ctx.moveTo(branch.segments[0].x, branch.segments[0].y);
          for (let i = 1; i < branch.segments.length; i++) {
            ctx.lineTo(branch.segments[i].x, branch.segments[i].y);
          }
        }
        ctx.stroke();

        // Mid violet glow
        ctx.strokeStyle = `hsla(275, 95%, 70%, ${baseOpacity * 0.32})`;
        ctx.lineWidth = branch.thickness * 4;

        ctx.beginPath();
        if (branch.segments.length > 0) {
          ctx.moveTo(branch.segments[0].x, branch.segments[0].y);
          for (let i = 1; i < branch.segments.length; i++) {
            ctx.lineTo(branch.segments[i].x, branch.segments[i].y);
          }
        }
        ctx.stroke();

        // Bright pink-white core
        ctx.strokeStyle = `hsla(322, 100%, 92%, ${baseOpacity * 0.85})`;
        ctx.lineWidth = branch.thickness;
        
        ctx.beginPath();
        if (branch.segments.length > 0) {
          ctx.moveTo(branch.segments[0].x, branch.segments[0].y);
          for (let i = 1; i < branch.segments.length; i++) {
            ctx.lineTo(branch.segments[i].x, branch.segments[i].y);
          }
        }
        ctx.stroke();
      });
    };
    
    const updateLightning = (bolt: LightningBolt, deltaTime: number) => {
      bolt.age += deltaTime;
      
      // Gentle fade in/out lifecycle
      const lifecycle = bolt.age / bolt.lifetime;
      if (lifecycle < 0.2) {
        // Fade in
        bolt.opacity = lifecycle / 0.2;
      } else if (lifecycle > 0.8) {
        // Fade out
        bolt.opacity = (1 - lifecycle) / 0.2;
      } else {
        // Stay visible with subtle breathing
        bolt.opacity = 0.7 + Math.sin(bolt.phase + bolt.age * 0.002) * 0.3;
      }
      
      bolt.phase += bolt.speed * deltaTime * 0.001;
      
      return bolt.age < bolt.lifetime;
    };
    
    const targetFPS = 24;
    const frameInterval = 1000 / targetFPS;
    let lastRenderTime = 0;
    
    const animate = (currentTime: number) => {
      animationFrameId = requestAnimationFrame(animate);
      
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      // Pause while hidden or during active scroll
      if (isPaused) {
        lastRenderTime = currentTime;
        return;
      }
      
      // Throttle to 24fps
      const elapsed = currentTime - lastRenderTime;
      if (elapsed < frameInterval) return;
      lastRenderTime = currentTime - (elapsed % frameInterval);
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Create new lightning bolts very rarely (more subtle)
      if (lightningBolts.length < 2 && Math.random() < 0.0008) {
        lightningBolts.push(createLightningBolt());
      }
      
      // Update and draw existing bolts
      lightningBolts = lightningBolts.filter(bolt => {
        const shouldKeep = updateLightning(bolt, deltaTime);
        if (shouldKeep && bolt.opacity > 0.01) {
          drawLightning(ctx, bolt);
        }
        return shouldKeep;
      });
    };
    
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    animate(0);
    
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  } catch (error) {
    console.error('CosmicLightningLayer: Error during initialization', error);
  }
}, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ 
        opacity: 0.35,
        zIndex: 3,
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        contain: 'strict'
      }}
    />
  );
};

export default CosmicLightningLayer;